import { useApolloClient, useMutation } from '@apollo/client';
import { Stack } from '@mui/material';
import { type FC, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { TimeOfDay } from '@focuslee/shared';
import { ButtonDock } from '@focuslee/shared-mini-app';
import { addQuestCompletion } from '../client/apollo';
import { questFragment } from '../client/fragments/quest';
import { RitualType, SubTask, SubTaskType } from '../client/generated/graphql';
import { completeQuestMutation } from '../client/mutations/completeQuest';
import { updateQuestMutation } from '../client/mutations/updateQuest';
import { ListQuest } from '../client/types';
import { Page } from '../components/Page';
import { RitualHeaderBanner } from '../components/ritual/RitualHeaderBanner';
import { RitualTasksList } from '../components/ritual/RitualTasksList';
import { RitualPageSkeleton } from '../components/ritual/skeleton/RitualPageSkeleton';
import { getNextRitualDueDate, mapTimeOfDayToGraphQLTimeOfDay } from '../helpers/quest';
import { getRitualConfig } from '../helpers/ritual';
import { getButtonThemeColors } from '../helpers/theme';
import { useCachedQuestWithFallback } from '../hooks/caching/quest';
import { journalEntryPath, ratingEntryPath } from '../navigation/paths';

export const RitualPage: FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const client = useApolloClient();
    const { id: questId } = useParams<{ id: string }>();
    const { quest, loading } = useCachedQuestWithFallback(questId);
    const [updateQuest] = useMutation(updateQuestMutation);
    const [completeQuest, { loading: completing }] = useMutation(completeQuestMutation, {
        fetchPolicy: 'no-cache'
    });
    const hasUnsavedSubTasksRef = useRef(false);
    const [subTasks, setSubTasks] = useState<SubTask[]>([]);

    const ritualType = quest?.ritualType as RitualType | undefined;
    const config = ritualType ? getRitualConfig(t)[ritualType] : null;

    useEffect(() => {
        hasUnsavedSubTasksRef.current = false;
    }, [questId]);

    useEffect(() => {
        if (!quest?.subTasks || hasUnsavedSubTasksRef.current) return;
        setSubTasks(quest.subTasks.map(t => ({ ...t })));
    }, [questId, quest?.subTasks]);

    const completedCount = subTasks.filter(t => t.done).length;
    const totalCount = subTasks.length;
    const remainingCount = totalCount - completedCount;
    const canComplete = totalCount === 0 || remainingCount === 0;

    const writeSubTasksToCache = useCallback((updatedSubTasks: SubTask[]) => {
        if (!quest || !questId) return;

        client.cache.writeFragment<ListQuest>({
            id: client.cache.identify({ __typename: 'Quest', id: questId }),
            fragment: questFragment,
            fragmentName: 'QuestFields',
            data: {
                ...quest,
                subTasks: updatedSubTasks,
            },
        });
    }, [client.cache, quest, questId]);

    const persistSubTasks = useCallback(async () => {
        if (!quest || !questId || !hasUnsavedSubTasksRef.current) return;

        await updateQuest({
            variables: {
                questId,
                name: quest.name,
                description: quest.description,
                categoryId: quest.categoryId,
                difficulty: quest.difficulty,
                goalId: quest.goalId ?? undefined,
                status: quest.status,
                timing: quest.timing ?? false,
                timedInterval: quest.timedInterval ?? 0,
                timerValueInMs: quest.timerValueInMs ?? 0,
                dueDate: quest.dueDate ?? undefined,
                repeat: quest.repeat
                    ? {
                        query: quest.repeat.query,
                        repeatingType: quest.repeat.repeatingType,
                        interval: quest.repeat.interval ?? undefined,
                        weekDays: quest.repeat.weekDays ?? [],
                        monthDays: quest.repeat.monthDays ?? [],
                        startHour: quest.repeat.startHour ?? undefined,
                        startMinute: quest.repeat.startMinute ?? undefined,
                    }
                    : undefined,
                subTasks: subTasks.map(({ __typename: _t, ...task }) => task),
            },
        });
        hasUnsavedSubTasksRef.current = false;
    }, [quest, questId, subTasks, updateQuest]);

    const handleTaskToggle = useCallback((index: number) => {
        if (!quest || !questId) return;
        
        const task = subTasks[index];
        
        if (task.taskType === SubTaskType.Journal) {
            const path = task.journalEntryId
                ? journalEntryPath.replace(':ritualId?', questId).replace(':journalEntryId?', task.journalEntryId)
                : journalEntryPath.replace(':ritualId?', questId).replace('/:journalEntryId?', '');
            navigate(path);
            return;
        }
        
        if (task.taskType === SubTaskType.Rating) {
            const path = task.ratingEntryId
                ? ratingEntryPath.replace(':ritualId?', questId).replace(':ratingEntryId?', task.ratingEntryId)
                : ratingEntryPath.replace(':ritualId?', questId).replace('/:ratingEntryId?', '');
            navigate(path);
            return;
        }
        
        const updatedSubTasks = subTasks.map((t, i) =>
            i === index ? { ...t, done: !t.done } : t
        );

        hasUnsavedSubTasksRef.current = true;
        setSubTasks(updatedSubTasks);
        writeSubTasksToCache(updatedSubTasks);
    }, [quest, questId, subTasks, navigate, writeSubTasksToCache]);

    const handleTaskAction = useCallback((index: number) => {
        const task = subTasks[index];
        
        if (task.taskType === SubTaskType.Journal) {
            const path = task.journalEntryId
                ? journalEntryPath.replace(':ritualId?', questId ?? '').replace(':journalEntryId?', task.journalEntryId)
                : journalEntryPath.replace(':ritualId?', questId ?? '').replace('/:journalEntryId?', '');
            navigate(path);
        } else if (task.taskType === SubTaskType.Rating) {
            const path = task.ratingEntryId
                ? ratingEntryPath.replace(':ritualId?', questId ?? '').replace(':ratingEntryId?', task.ratingEntryId)
                : ratingEntryPath.replace(':ritualId?', questId ?? '').replace('/:ratingEntryId?', '');
            navigate(path);
        }
    }, [subTasks, questId, navigate]);

    const navigateBack = useCallback(async () => {
        try {
            await persistSubTasks();
        } catch (error) {
            console.error('Error saving ritual subtasks:', error);
        }
        navigate(-1);
    }, [navigate, persistSubTasks]);

    const handleCompleteRitual = useCallback(async () => {
        if (!quest || !questId || !canComplete || completing) return;
        
        try {
            await persistSubTasks();

            const ritualType = quest.ritualType as 'Morning' | 'Evening';
            const newDueDate = getNextRitualDueDate(quest, ritualType);
            const timeOfDay: TimeOfDay = ritualType === 'Morning' ? 'MorningRitual' : 'EveningRitual';
            const result = await completeQuest({
                variables: {
                    questId: questId,
                    dueDate: newDueDate,
                    timerValueInMs: 0,
                    timeOfDay: mapTimeOfDayToGraphQLTimeOfDay(timeOfDay),
                },
            });
            
            const completionData = result.data?.completeQuest;
            if (completionData) {
                addQuestCompletion(completionData);
            }
            
            navigate(-1);
        } catch (error) {
            console.error('Error completing ritual:', error);
        }
    }, [quest, questId, canComplete, completing, completeQuest, persistSubTasks, navigate]);

    if (loading || !quest || !config) {
        return <RitualPageSkeleton />;
    }

    const canCompleteNow = canComplete && !completing;

    return (
        <Page
            back={true}
            insetEnabled={false}
            footer={
                <ButtonDock
                    secondary={{ text: t('actions.back'), onClick: navigateBack, navigation: 'back' }}
                    main={{
                        text: t('actions.complete'),
                        onClick: handleCompleteRitual,
                        enabled: canCompleteNow,
                        loading: completing,
                        bgColor: canCompleteNow ? config.buttonBgEnabled : getButtonThemeColors(false).backgroundColor,
                    }}
                />
            }
        >
            <Stack height="100%" sx={{ background: 'var(--tgui--secondary_bg_color)', paddingTop: 'var(--tg-viewport-content-safe-area-inset-top, 0px)' }}>
                <RitualHeaderBanner
                    config={config}
                    completedCount={completedCount}
                    totalCount={totalCount}
                />
                <RitualTasksList
                    tasks={subTasks}
                    config={config}
                    onToggle={handleTaskToggle}
                    onAction={handleTaskAction}
                />
            </Stack>
        </Page>
    );
};
